import i18n from '@/i18n';
import {
  ProjectDetailsActions,
  ProjectDetailsMutations,
  ProjectDetailsGetters
} from './project-details.keys';
import { projectDetailsService } from './project-details.service';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import { TolMessagesActions } from '@/app/features/tol-messages/tol-messages.keys';

export const getDefaultState = () => {
  return {
    isDetailsLoading: false,
    isLoadingAttachments: false,
    isSavingProject: false,
    currentProject: {},
    currentProjectAttachments: [],
    eventTypeValidationErrors: {},
    projectValidationErrors: {},
    projectClientContactsSortOrder: {
      sortBy: 'role',
      sortDirection: 'desc'
    },
    projectTESContactsSortOrder: {
      sortBy: 'role',
      sortDirection: 'desc'
    },
    projectTechniciansSortOrder: {
      sortBy: 'first_name',
      sortDirection: 'desc'
    },
    projectEventTypesSortOrder: {
      sortBy: 'name',
      sortDirection: 'desc'
    },
    projectPurchaseOrdersSortOrder: {
      sortBy: 'purchase_order_number',
      sortDirection: 'desc'
    },
    projectTravelRatesSortOrder: {
      sortBy: 'description',
      sortDirection: 'desc'
    }
  };
};

export const projectDetailsStore = {
  state: getDefaultState(),
  getters: {
    [ProjectDetailsGetters.CurrentProjectAttachments]: state => state.currentProjectAttachments,
    [ProjectDetailsGetters.IsProjectDetailsLoading]: state => {
      return state.isDetailsLoading;
    },
    [ProjectDetailsGetters.IsProjectAttachmentsLoading]: state => {
      return state.isLoadingAttachments;
    },
    [ProjectDetailsGetters.IsSavingProject]: state => {
      return state.isSavingProject;
    },
    [ProjectDetailsGetters.CurrentProject]: state => {
      return state.currentProject;
    },
    [ProjectDetailsGetters.ProjectValidationErrors]: state => {
      return state.projectValidationErrors;
    },
    [ProjectDetailsGetters.EventTypeValidationErrors]: state => state.eventTypeValidationErrors,
    [ProjectDetailsGetters.ProjectClientContactsSortOrder]: state => {
      return state.projectClientContactsSortOrder;
    },
    [ProjectDetailsGetters.ProjectTESContactsSortOrder]: state => {
      return state.projectTESContactsSortOrder;
    },
    [ProjectDetailsGetters.ProjectTechniciansSortOrder]: state => {
      return state.projectTechniciansSortOrder;
    },
    [ProjectDetailsGetters.ProjectEventTypesSortOrder]: state => {
      return state.projectEventTypesSortOrder;
    },
    [ProjectDetailsGetters.ProjectPurchaseOrdersSortOrder]: state => {
      return state.projectPurchaseOrdersSortOrder;
    },
    [ProjectDetailsGetters.ProjectTravelRatesSortOrder]: state => {
      return state.projectTravelRatesSortOrder;
    }
  },
  mutations: {
    [ProjectDetailsMutations.SetProjectDetailsLoading]: (state, isLoading = true) => {
      state.isDetailsLoading = isLoading;
    },
    [ProjectDetailsMutations.SetProjectAttachmentsLoading]: (state, isLoading = true) => {
      state.isProjectAttachmentsLoading = isLoading;
    },
    [ProjectDetailsMutations.SetIsSavingProject]: (state, isSaving) => {
      state.isSavingProject = isSaving;
    },
    [ProjectDetailsMutations.SetProject]: (state, project) => {
      state.currentProject = project;
    },
    [ProjectDetailsMutations.FetchProjectAttachmentsSuccess]: (state, attachments) => {
      state.currentProjectAttachments = attachments;
    },
    [ProjectDetailsMutations.SetProjectValidationErrorsSuccess]: (state, errors) => {
      state.projectValidationErrors = errors;
    },
    [ProjectDetailsMutations.DeleteProjectValidationErrorKeySuccess]: (state, key) => {
      const updatedErrors = Object.assign({}, state.projectValidationErrors);
      delete updatedErrors[key];
      state.projectValidationErrors = updatedErrors;
    },
    [ProjectDetailsMutations.SetEventTypeValidationErrorsSuccess]: (state, errors) => {
      state.eventTypeValidationErrors = errors;
    },
    [ProjectDetailsMutations.DeleteEventTypeValidationErrorKeySuccess]: (state, key) => {
      const updatedErrors = Object.assign({}, state.eventTypeValidationErrors);
      delete updatedErrors[key];
      state.eventTypeValidationErrors = updatedErrors;
    },
    [ProjectDetailsMutations.SetProjectClientContactsSortOrder]: (state, sortOrder) => {
      state.projectClientContactsSortOrder = sortOrder;
    },
    [ProjectDetailsMutations.SetProjectTESContactsSortOrder]: (state, sortOrder) => {
      state.projectTESContactsSortOrder = sortOrder;
    },
    [ProjectDetailsMutations.SetProjectEventTypesSortOrder]: (state, sortOrder) => {
      state.projectEventTypesSortOrder = sortOrder;
    },
    [ProjectDetailsMutations.SetProjectPurchaseOrdersSortOrder]: (state, sortOrder) => {
      state.projectPurchaseOrdersSortOrder = sortOrder;
    },
    [ProjectDetailsMutations.SetProjectTravelRatesSortOrder]: (state, sortOrder) => {
      state.projectTravelRatesSortOrder = sortOrder;
    },
    [ProjectDetailsMutations.SetProjectTechniciansSortOrder]: (state, sortOrder) => {
      state.projectTechniciansSortOrder = sortOrder;
    },
    [ProjectDetailsMutations.SortProjectClientContacts]: (state, sortOrder) => {
      state.currentProject.client_contacts.sort((a, b) => {
        if (sortOrder.sortDirection === 'desc') {
          return a[sortOrder.sortBy].localeCompare(b[sortOrder.sortBy]);
        }
        return b[sortOrder.sortBy].localeCompare(a[sortOrder.sortBy]);
      });
    },
    [ProjectDetailsMutations.SortProjectTESContacts]: (state, sortOrder) => {
      state.currentProject.tes_contacts.sort((a, b) => {
        if (sortOrder.sortDirection === 'desc') {
          return a[sortOrder.sortBy].localeCompare(b[sortOrder.sortBy]);
        }
        return b[sortOrder.sortBy].localeCompare(a[sortOrder.sortBy]);
      });
    },
    [ProjectDetailsMutations.SortProjectTechnicians]: (state, sortOrder) => {
      state.currentProject.technicians.sort((a, b) => {
        if (sortOrder.sortDirection === 'desc') {
          return a[sortOrder.sortBy].localeCompare(b[sortOrder.sortBy]);
        }
        return b[sortOrder.sortBy].localeCompare(a[sortOrder.sortBy]);
      });
    },
    [ProjectDetailsMutations.SortProjectEventTypes]: (state, sortOrder) => {
      state.currentProject.event_types.sort((a, b) => {
        if (
          sortOrder.sortBy === 'estimated_hours_onsite' ||
          !a[sortOrder.sortBy] ||
          !b[sortOrder.sortBy]
        ) {
          if (sortOrder.sortDirection === 'desc') {
            return a[sortOrder.sortBy] - b[sortOrder.sortBy];
          }
          return b[sortOrder.sortBy] - a[sortOrder.sortBy];
        } else {
          if (sortOrder.sortDirection === 'desc') {
            return a[sortOrder.sortBy].localeCompare(b[sortOrder.sortBy]);
          }
          return b[sortOrder.sortBy].localeCompare(a[sortOrder.sortBy]);
        }
      });
    },
    [ProjectDetailsMutations.SortProjectPurchaseOrders]: (state, sortOrder) => {
      state.currentProject.purchase_orders.sort((a, b) => {
        if (
          sortOrder.sortBy === 'purchase_order_number' ||
          !a[sortOrder.sortBy] ||
          !b[sortOrder.sortBy]
        ) {
          if (sortOrder.sortDirection === 'desc') {
            return a[sortOrder.sortBy] - b[sortOrder.sortBy];
          }
          return b[sortOrder.sortBy] - a[sortOrder.sortBy];
        } else {
          if (sortOrder.sortDirection === 'desc') {
            return a[sortOrder.sortBy].isBefore(b[sortOrder.sortBy]);
          }
          return b[sortOrder.sortBy].isBefore(a[sortOrder.sortBy]);
        }
      });
    },
    [ProjectDetailsMutations.SortProjectTravelRates]: (state, sortOrder) => {
      state.currentProject.travel_rates.sort((a, b) => {
        if (sortOrder.sortBy === 'description' && a[sortOrder.sortBy] && !b[sortOrder.sortBy]) {
          if (sortOrder.sortDirection === 'desc') {
            return a[sortOrder.sortBy].localeCompare(b[sortOrder.sortBy]);
          }
          return b[sortOrder.sortBy].localeCompare(a[sortOrder.sortBy]);
        } else {
          if (sortOrder.sortDirection === 'desc') {
            return a[sortOrder.sortBy] - b[sortOrder.sortBy];
          }
          return b[sortOrder.sortBy] - a[sortOrder.sortBy];
        }
      });
    }
  },
  actions: {
    [ProjectDetailsActions.FetchProject]: ({ commit, dispatch }, referenceNumber) => {
      commit(ProjectDetailsMutations.SetProjectDetailsLoading);
      return projectDetailsService
        .fetchProject(referenceNumber)
        .then(project => {
          commit(ProjectDetailsMutations.SetProject, project);
        })
        .then(() => {
          commit(ProjectDetailsMutations.SetProjectDetailsLoading, false);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('projects.details.errors.fetchProject')
          });
          commit(ProjectDetailsMutations.SetProjectDetailsLoading, false);
        });
    },
    [ProjectDetailsActions.SaveProject]: ({ commit, dispatch }, projectData) => {
      commit(ProjectDetailsMutations.SetIsSavingProject, true);

      projectDetailsService
        .updateProject(projectData)
        .then(updatedProject => {
          commit(ProjectDetailsMutations.SetProject, updatedProject);
          dispatch(TolMessagesActions.SetMessages, {
            text: i18n.t('projects.details.saveSuccess', { name: updatedProject.name })
          });
        })
        .catch(error => {
          const errorItems = Object.keys(error.data);
          let items = [];
          for (let item of errorItems) {
            if (item !== 'statusCode') {
              items = items.concat(error.data[item]);
            }
          }
          dispatch(TolErrorsActions.SetError, { items });
        });

      commit(ProjectDetailsMutations.SetIsSavingProject, false);
    },
    [ProjectDetailsActions.CreateEventType]: ({ dispatch }, request) => {
      projectDetailsService
        .createEventType(request)
        .then(updatedEventType => {
          dispatch(TolMessagesActions.SetMessages, {
            text: i18n.t('projects.eventTypes.createSuccess', { name: updatedEventType.name })
          });
          dispatch(ProjectDetailsActions.FetchProject, request.projectReferenceNumber);
        })
        .catch(error => {
          const errorItems = Object.keys(error.data);
          let items = [];
          for (let item of errorItems) {
            if (item !== 'statusCode') {
              items = items.concat(error.data[item]);
            }
          }
          dispatch(TolErrorsActions.SetError, { items });
        });
    },
    [ProjectDetailsActions.SaveProjectClientContact]: ({ dispatch }, projectClientContact) => {
      projectDetailsService
        .updateProjectClientContact(projectClientContact)
        .then(() => {
          dispatch(TolMessagesActions.SetMessages, {
            text: i18n.t('projects.details.contacts.saveSuccess')
          });
        })
        .catch(error => {
          const messages = Object.values(error.data);
          let items = [];
          for (let item of messages) {
            items = items.concat(item);
          }
          dispatch(TolErrorsActions.SetError, { items });
        });
      dispatch(ProjectDetailsActions.FetchProject, projectClientContact.projectReferenceNumber);
    },
    [ProjectDetailsActions.SaveProjectTesContact]: ({ dispatch }, projectTesContact) => {
      projectDetailsService
        .updateProjectTesContact(projectTesContact)
        .then(() => {
          dispatch(TolMessagesActions.SetMessages, {
            text: i18n.t('projects.details.contacts.saveSuccess')
          });
        })
        .catch(error => {
          const messages = Object.values(error.data);
          let items = [];
          for (let item of messages) {
            items = items.concat(item);
          }
          dispatch(TolErrorsActions.SetError, { items });
        });
      dispatch(ProjectDetailsActions.FetchProject, projectTesContact.projectReferenceNumber);
    },
    [ProjectDetailsActions.SetProjectClientContactsSortOrder]: ({ commit }, sortOrder) => {
      commit(ProjectDetailsMutations.SetProjectClientContactsSortOrder, sortOrder);
      commit(ProjectDetailsMutations.SortProjectClientContacts, sortOrder);
    },
    [ProjectDetailsActions.SetProjectTESContactsSortOrder]: ({ commit }, sortOrder) => {
      commit(ProjectDetailsMutations.SetProjectTESContactsSortOrder, sortOrder);
      commit(ProjectDetailsMutations.SortProjectTESContacts, sortOrder);
    },
    [ProjectDetailsActions.SetProjectTechniciansSortOrder]: ({ commit }, sortOrder) => {
      commit(ProjectDetailsMutations.SetProjectTechniciansSortOrder, sortOrder);
      commit(ProjectDetailsMutations.SortProjectTechnicians, sortOrder);
    },
    [ProjectDetailsActions.SetProjectEventTypesSortOrder]: ({ commit }, sortOrder) => {
      commit(ProjectDetailsMutations.SetProjectEventTypesSortOrder, sortOrder);
      commit(ProjectDetailsMutations.SortProjectEventTypes, sortOrder);
    },
    [ProjectDetailsActions.SetProjectPurchaseOrdersSortOrder]: ({ commit }, sortOrder) => {
      commit(ProjectDetailsMutations.SetProjectPurchaseOrdersSortOrder, sortOrder);
      commit(ProjectDetailsMutations.SortProjectPurchaseOrders, sortOrder);
    },
    [ProjectDetailsActions.SetProjectTravelRatesSortOrder]: ({ commit }, sortOrder) => {
      commit(ProjectDetailsMutations.SetProjectTravelRatesSortOrder, sortOrder);
      commit(ProjectDetailsMutations.SortProjectTravelRates, sortOrder);
    },
    [ProjectDetailsActions.FetchProjectAttachments]: ({ state, commit }, referenceNumber) => {
      return projectDetailsService
        .fetchProjectAttachments(referenceNumber, state.projectAttachmentsSortOrder)
        .then(attachments => {
          commit(ProjectDetailsMutations.FetchProjectAttachmentsSuccess, attachments);
        });
    }
  }
};
